import * as React from "react";
import { cn } from "@/lib/utils";

// Ported from careers.co.jp v2 Avatar — solid color, no gradients. Without a
// photo, picks one of five deterministic tones from the alt name so the same
// person gets the same color on every screen. `variant="brand"` forces AGP
// blue (used for the current user / "me").

const sizeStyles = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-[11px]",
  md: "h-10 w-10 text-xs",
  lg: "h-14 w-14 text-sm",
  xl: "h-20 w-20 text-base",
  "2xl": "h-24 w-24 text-lg",
} as const;

export type AvatarSize = keyof typeof sizeStyles;

// Tones 3–5 are fixed hexes (amber / orange / zinc from the careers kit);
// AGP's tailwind config has no reaction tokens to alias them to.
const TONE_CLASSES = {
  1: "bg-brand-500 text-white",
  2: "bg-success text-white",
  3: "bg-[#F7B928] text-[#1A1A1A]",
  4: "bg-[#E9710F] text-white",
  5: "bg-[#52525B] text-white",
} as const;

type Tone = keyof typeof TONE_CLASSES;

export type AvatarVariant = "auto" | "brand";

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  src?: string | null;
  alt?: string;
  fallback?: string;
  size?: AvatarSize;
  ringed?: boolean;
  variant?: AvatarVariant;
}

function initialsFrom(name?: string): string {
  if (!name) return "?";
  const trimmed = name.trim();
  // Build initials only from words that begin with a letter or number, so
  // stray punctuation doesn't leak into the avatar ("Dan (draft)" → "D").
  const parts = trimmed
    .split(/[\s_]+/)
    .filter((p) => /^[\p{L}\p{N}]/u.test(p))
    .slice(0, 2);
  if (parts.length > 0) {
    return parts.map((p) => p[0]!.toUpperCase()).join("");
  }
  const first = trimmed.match(/[\p{L}\p{N}]/u);
  return first ? first[0]!.toUpperCase() : "?";
}

function avatarToneFor(name: string | undefined): Tone {
  if (!name) return 1;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return ((h % 5) + 1) as Tone;
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ src, alt, fallback, size = "md", ringed, variant = "auto", className, ...props }, ref) => {
    const [errored, setErrored] = React.useState(false);
    const showImg = src && !errored;
    const label = fallback ?? initialsFrom(alt);
    const tone = variant === "brand" ? TONE_CLASSES[1] : TONE_CLASSES[avatarToneFor(alt)];
    return (
      <span
        ref={ref}
        className={cn("relative inline-flex shrink-0", sizeStyles[size])}
        aria-label={alt}
        {...props}
      >
        <span
          className={cn(
            "flex h-full w-full select-none items-center justify-center overflow-hidden rounded-full font-semibold",
            tone,
            ringed && "ring-2 ring-surface ring-offset-1 ring-offset-border",
            className,
          )}
        >
          {showImg ? (
            <img
              src={src}
              alt={alt ?? ""}
              className="h-full w-full object-cover"
              onError={() => setErrored(true)}
            />
          ) : (
            <span aria-hidden>{label}</span>
          )}
        </span>
      </span>
    );
  },
);
Avatar.displayName = "Avatar";

export { initialsFrom };
